import type { Capability, CurrentUser } from '@/types/domain'

/* Capability tests for the interface.
 *
 * Roles became runtime-definable with migration 0021, which superseded AD-7's
 * fixed five. That changed what the client is allowed to ask. It used to ask
 * "is this user Finance" and render accordingly, because the five roles were
 * knowable at build time; a founder can now create a role this build has never
 * heard of, so that question has no useful answer — a custom role matches none
 * of the five arms and a `switch` on it silently falls through to whichever
 * branch happens to be last.
 *
 * So the interface asks "may this user issue an invoice" instead, and the
 * server answers by listing the capabilities it resolved on this very request.
 *
 * NONE OF THIS IS AUTHORIZATION. Every capability tested here is re-checked
 * server-side on the request that acts on it (AD-21, FR-56). Hiding a button
 * the server would refuse is a courtesy to the user, not a control — a client
 * that granted itself a capability would get a 403 for its trouble. The reason
 * to be careful here is that a WRONGLY HIDDEN button looks like a broken
 * product, not that a wrongly shown one is a breach.
 */

/** Whether a user holds a capability.
 *
 * A null or undefined user answers false, which is what the loading state and
 * the signed-out state should both render as: an interface that shows every
 * button for the instant before the session resolves is worse than one that
 * shows none and fills in.
 *
 * The superuser short-circuits, so a founder's interface is complete even for
 * capabilities added after this build shipped. */
export function can(user: CurrentUser | null | undefined, capability: Capability): boolean {
  if (!user) return false
  if (user.isSuperuser) return true
  return user.capabilities.includes(capability)
}

/** Whether a user holds EVERY one of the given capabilities.
 *
 * For actions gated on two at once, such as an invoice that must be drafted
 * before it can be issued. */
export function canAll(
  user: CurrentUser | null | undefined,
  ...capabilities: Capability[]
): boolean {
  return capabilities.every((c) => can(user, c))
}

/** Whether a user holds ANY of the given capabilities.
 *
 * For a section that several distinct permissions can open — a navigation entry
 * that leads to a screen with more than one thing on it. */
export function canAny(
  user: CurrentUser | null | undefined,
  ...capabilities: Capability[]
): boolean {
  return capabilities.some((c) => can(user, c))
}

/* The capability keys this client tests, spelled once.
 *
 * Duplicated from the server's registry in platform/authz, the same way the
 * role strings were duplicated before it — the client cannot import Go, and a
 * key spelled inline at a call site is a key that gets misspelled. A typo here
 * fails CLOSED: `can()` finds no such capability in the list and hides the
 * button, which is visible immediately rather than silently permissive.
 *
 * Only the keys the interface actually branches on are listed. The full
 * vocabulary is served by GET /api/admin/capabilities for the role editor,
 * which renders whatever the server registers rather than this list. */
export const CAP = {
  quoteCreate: 'quote.create',
  quoteSend: 'quote.send',
  /* Staffing a quote, and — since S-2a — asking a colleague for its price and
     withdrawing that request (`POST`/`DELETE /quotes/:id/pricing-request`,
     and the `GET /users/pricing-desk` roster the request picks from). */
  quoteTeamAssign: 'quote.team.assign',
  /* «Расчёт готов» (S-2a): marking a quote's price ready. Seeded to Pricing
     only; a custom pricing role holds it once the role editor grants it. The
     same key decides who the request's picker may offer — on the server. */
  quotePricingProvide: 'quote.pricing.provide',

  caseCreate: 'case.create',
  caseCloseOperational: 'case.close.operational',
  caseCloseFinancial: 'case.close.financial',
  caseRouteEdit: 'case.route.edit',
  caseTeamAssign: 'case.team.assign',
  caseReopen: 'case.reopen',

  /* Route templates (S-10, 0072): create, amend and hide them — the leg page's
     «Сохранить как шаблон» and «+ Новый», and the Settings «Маршруты» tab
     beside `adminSettingsManage`. Seeded to Operations and Pricing. Deleting a
     template stays on `adminSettingsManage` alone. */
  routeTemplateManage: 'route.template.manage',

  directoryClientCreate: 'directory.client.create',
  directoryClientManage: 'directory.client.manage',
  directoryTransportManage: 'directory.transport.manage',
  /* Whether a counterparty's contact details (phone, email, handles) are served
     unsuppressed. Without it the server also IGNORES the contacts of a client or
     participant update, so the sheets render them read-only on an existing
     record (B4) — a save must not write back the blanks a suppressed field
     arrived as. */
  directoryContactRead: 'directory.contact.read',

  /* Counterparties (S-22b, migration 0076 renamed the four directory
     participant keys in place). There is no row scope behind them: the
     directory is the company's. READ is the whole visibility rule — without it
     the list is empty and a record by id is not found. CREATE adds one and
     edits or deactivates any one; MANAGE deletes and merges, seeded to nobody.
     The server asks for READ beside each of the other two, so a client gate on
     either act must ask for both (`canAll`). */
  counterpartyRead: 'counterparty.read',
  counterpartyCreate: 'counterparty.create',
  counterpartyManage: 'counterparty.manage',

  financeExpenseRecord: 'finance.expense.record',
  financePaymentRecord: 'finance.payment.record',
  /* The client door on the per-case payment write (S-21, 0084): record the
     money the case's own client sent and allocate it to the case's invoices,
     debit notes and tranches in one act. Sales holds it; the server forces the
     direction and the party, and a holder without `financePaymentConfirm`
     records a pending payment. Never a widening of `financePaymentRecord`. */
  financePaymentClient: 'finance.payment.client',
  /* The outbound act on a leg — recognise the carrier's debt, record the
     payment and allocate it, in one request (0059, spec L7). Deliberately NOT
     a widening of `financePaymentRecord`: Operations gets the one payment their
     work needs and nothing else — not the inbound side, not the cross-case
     register. */
  financePaymentCarrier: 'finance.payment.carrier',
  /* The second pair of eyes: confirm a recorded payment, or delete a pending
     one with a reason. Finance's, and the only thing that reveals either
     control — hiding them is a courtesy, and the server re-checks on the
     request (AD-21). */
  financePaymentConfirm: 'finance.payment.confirm',
  financeReceivablesRead: 'finance.receivables.read',

  /* Finance's workspace on a case — «Счета клиенту» under «Деньги» since S-11
     (`?section=documents` is now an alias that lands on it) — and everything
     on it. RENDER is the gate on the whole of a document's draft life — the
     list, the raise, the edits, the PDF — so a role holding either render key
     gets the workspace (`givenFinanceWorkspace`, features/cases/caseSections); ISSUE is the separate, binding act (the server
     asks for both on an invoice, for issue alone on a debit note). */
  documentInvoiceRender: 'document.invoice.render',
  documentInvoiceIssue: 'document.invoice.issue',
  documentDebitNoteRender: 'document.debitnote.render',
  documentDebitNoteIssue: 'document.debitnote.issue',
  documentOfferRender: 'document.offer.render',
  /* The archive LIST — what paper a reachable record produced (kind, number,
     dates, entity name) — and the bytes of an offer or a status update. A
     reader holding it but neither render key gets the same section read-only,
     as «Бумаги работы» (`CaseArchiveList`); invoice and debit-note PDFs stay on
     the render keys (`mayOpenArchivedKind`). All four seeded roles hold it, so
     a role holding none of the three keys is a custom one, and it gets no
     section at all. */
  documentArchiveRead: 'document.archive.read',

  adminUsersRead: 'admin.users.read',
  adminUsersManage: 'admin.users.manage',
  adminRolesRead: 'admin.roles.read',
  adminRolesManage: 'admin.roles.manage',
  adminSettingsManage: 'admin.settings.manage',
} as const satisfies Record<string, Capability>

/* The scopable surfaces, spelled once (#129, N-08).
 *
 * Mirrors `backend/platform/scope/surface.go` (`can.test.ts` reads it). The
 * session names the surfaces its role REACHES — any level at all — in
 * `CurrentUser.surfaces`, and `reaches()` below tests membership.
 *
 * WHOLE-SURFACE NAVIGATION AND A LIST'S REFUSAL ONLY. A surface left out of
 * the session is «Нет доступа» in the role editor, so the section leaves the
 * menu and a deep link to it is refused instead of rendering an empty list.
 * It is NEVER a per-record decision: whether a case's money is shown stays on
 * the record (`moneyUnavailable`), because the server decides that per case.
 * No level (own / assigned / all) is served or compared — the WHERE clause
 * narrows the rows, and the client has nothing to decide with it.
 *
 * Like `can()`, an affordance and never authorization (AD-21): the server
 * scopes every read whatever is rendered here. */
export const SURFACE = {
  quotes: 'quotes',
  cases: 'cases',
  /* Declared for the mirror; no menu item or page reads it — case money is
     decided per record (`moneyUnavailable`). */
  caseMoney: 'case-money',
  /* Declared for the mirror and inert. Finance stays on
     `CAP.financeReceivablesRead`, the key every Finance read is guarded by;
     gating it on this surface would make the client stricter than the server. */
  crossCaseFinance: 'cross-case-finance',
  clients: 'clients',
} as const

export type Surface = (typeof SURFACE)[keyof typeof SURFACE]

/** Whether a user's role reaches a surface at all.
 *
 * False for a null user (the loading and signed-out state, as `can()`), true
 * for the superuser (who reaches every surface server-side whatever its rows
 * say), and otherwise membership in the served list. A session without the
 * list reaches nothing: a hidden section is visible at once, while an empty
 * list behind a menu item reads as lost data. */
export function reaches(user: CurrentUser | null | undefined, surface: Surface): boolean {
  if (!user) return false
  if (user.isSuperuser) return true
  return user.surfaces?.includes(surface) ?? false
}
